import {request, HTTPError} from "../api";
import {validateGridDocument, type GridDocument} from "./document";
import {GRID_PROJECT_ID_PATTERN} from "./project-contract";
import {validateSavedGrid, type SavedGrid} from "./snapshots";

export type ProjectHead = {projectId: string; revision: number; digest: string; createdAt: string; editPath: string};
export type SavedProject = ProjectHead & {document: GridDocument; updatedAt: string};
export type ProjectReceipt = ProjectHead & {savedAt: string; replayed: boolean; headRequired: boolean; document?: GridDocument};
export type ProjectSummary = {projectId: string; title: string; bannerCount: number; revision: number; createdAt: string; updatedAt: string; trashedAt: string | null; editPath: string};
export type ProjectPage = {projects: ProjectSummary[]; nextCursor: string | null};
export type ProjectSaveAttempt = {document: GridDocument; key: string; target: {projectId: string; revision: number} | null};
export class ProjectRequestError extends Error {
  constructor(public status: number, public code: string, message: string) {super(message);}
}
const uuid = new RegExp(GRID_PROJECT_ID_PATTERN);
const date = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value));
function checkHead(value: any) {
  if (!value || !uuid.test(value.projectId) || !Number.isSafeInteger(value.revision) || value.revision < 1 ||
    !/^[a-f0-9]{64}$/.test(value.digest) || !date(value.createdAt) || value.editPath !== `/grid/projects/${value.projectId}`) throw new Error("Invalid Grid project response");
}
async function projectRequest(path: string, options = {}) {
  try {return await request(`/api/v3/grid-projects${path}`, {credentials: "include", ...options});} catch (error) {
    if (error instanceof HTTPError) throw new ProjectRequestError(error.status, error.data.code,
      typeof error.data.message === "string" ? error.data.message : error.message);
    throw error;
  }
}
export async function loadGridProject(projectId: string, signal?: AbortSignal): Promise<SavedProject> {
  const timeout = AbortSignal.timeout(30_000);
  const value = await projectRequest(`/${encodeURIComponent(projectId)}`, {signal: signal ? AbortSignal.any([signal, timeout]) : timeout});
  checkHead(value); validateGridDocument(value.document);
  if (!date(value.updatedAt)) throw new Error("Invalid Grid project timestamp");
  return value;
}
export async function saveGridProject(attempt: ProjectSaveAttempt): Promise<ProjectReceipt> {
  const value = await projectRequest(attempt.target ? `/${encodeURIComponent(attempt.target.projectId)}` : "", {
    method: attempt.target ? "PUT" : "POST", headers: {"Idempotency-Key": attempt.key, ...(attempt.target ? {"If-Match": `"${attempt.target.revision}"`} : {})},
    body: JSON.stringify({document: attempt.document}), signal: AbortSignal.timeout(30_000),
  });
  checkHead(value);
  if (!date(value.savedAt) || typeof value.replayed !== "boolean" || value.headRequired !== value.replayed ||
    (attempt.target && value.projectId !== attempt.target.projectId)) throw new Error("Invalid Grid save receipt");
  if (!value.headRequired) validateGridDocument(value.document);
  return value;
}
export async function listGridProjects(q: string, cursor: string | null, signal?: AbortSignal, trash = false): Promise<ProjectPage> {
  const params = new URLSearchParams({q, ...(trash ? {trash: "true"} : {})});
  if (cursor) params.set("cursor", cursor);
  const value = await projectRequest(`?${params}`, {signal});
  if (!value || !Array.isArray(value.projects) || !(value.nextCursor === null || typeof value.nextCursor === "string")) throw new Error("Invalid Grid projects list");
  for (const project of value.projects) {
    if (!uuid.test(project.projectId) || project.editPath !== `/grid/projects/${project.projectId}` || typeof project.title !== "string" ||
      !Number.isSafeInteger(project.bannerCount) || project.bannerCount < 0 || !Number.isSafeInteger(project.revision) || project.revision < 1 ||
      !date(project.createdAt) || !date(project.updatedAt) || !(project.trashedAt === null || date(project.trashedAt))) throw new Error("Invalid Grid project metadata");
  }
  return value;
}

export type ProjectOperationAttempt = {key: string} & (
  {action: "import"; snapshotId: string} | {action: "rename" | "duplicate"; projectId: string; revision: number; title: string} |
  {action: "trash" | "restore"; projectId: string; revision: number});
export async function operateGridProject(attempt: ProjectOperationAttempt): Promise<ProjectHead> {
  const importing = attempt.action === "import";
  const path = importing ? "/import" : `/${attempt.projectId}${["duplicate", "restore"].includes(attempt.action) ? `/${attempt.action}` : ""}`;
  const body = attempt.action === "import" ? {snapshotId: attempt.snapshotId} : attempt.action === "rename" || attempt.action === "duplicate" ? {title: attempt.title} : {};
  const value = await projectRequest(path, {method: importing || ["duplicate", "restore"].includes(attempt.action) ? "POST" : attempt.action === "trash" ? "DELETE" : "PATCH",
    headers: {"Idempotency-Key": attempt.key, ...(!importing ? {"If-Match": `"${attempt.revision}"`} : {})}, body: JSON.stringify(body), signal: AbortSignal.timeout(30_000)});
  checkHead(value);
  if (typeof value.replayed !== "boolean" || !date(value.savedAt)) throw new Error("Invalid project operation receipt");
  return value;
}
export async function shareGridProject(target: {projectId: string; revision: number; key: string}): Promise<SavedGrid> {
  const value = await projectRequest(`/${target.projectId}/share`, {method: "POST", headers: {"Idempotency-Key": target.key, "If-Match": `"${target.revision}"`}, body: "{}", signal: AbortSignal.timeout(30_000)});
  if (value.projectId !== target.projectId || value.revision !== target.revision) throw new Error("Share did not acknowledge the selected project revision");
  return validateSavedGrid(value);
}
export function projectSnapshotId(input: string, origin: string): string {
  const value = input.trim();
  if (/^[a-f0-9]{64}$/.test(value)) return value;
  const url = new URL(value, origin), match = /^\/grid\/([a-f0-9]{64})$/.exec(url.pathname);
  if (url.origin !== origin || url.username || url.password || url.search || url.hash || !match) throw new Error("Use a snapshot link from this Coverbaker or its 64-character snapshot ID");
  return match[1];
}
